import * as THREE from 'three';
import { seeded } from './kit.js';

/**
 * A subway tile texture, drawn rather than loaded.
 *
 * The arena is built from primitives and ships no external models, which leaves
 * a tiled wall as either ten thousand little boxes or a pattern painted onto
 * one. It is painted. A canvas is not a model: nothing is fetched, the whole
 * thing is thirty lines, and the tile size is a number in the arena's own JSON
 * rather than a property of a file somebody has to find.
 *
 * Returns null where there is no canvas to draw on — the unit suite runs in
 * `node` — and every caller treats that as "use the flat colour", so an arena
 * still builds without one.
 */

const PIXELS_PER_METRE = 128;
/** Tiles drawn per canvas, each way: enough that the shading does not repeat in a pattern. */
const ACROSS = 8;

/**
 * Glazed tile, and since step 98 not one flat glaze: each tile its own
 * shade, a few stained, and a roughness map beside the colour —
 * the glaze shiny, the grout dull — so the strip lights catch the wall tile by
 * tile instead of washing it flat. The roughness rides on the colour texture
 * as `roughnessTexture` (not `userData`, which `clone` copies through JSON),
 * and `tileFace` repeats either.
 */
export function createTileTexture({
  tile = '#c8d0d8',
  grout = '#79838f',
  /** Metres. A real station tile is about this. */
  tileWidth = 0.2,
  tileHeight = 0.1,
  groutWidth = 0.012,
  /** How far each row is shifted from the one below, as a fraction of a tile. */
  offset = 0.5,
  seed = 5,
} = {}) {
  const width = Math.round(tileWidth * ACROSS * PIXELS_PER_METRE);
  const height = Math.round(tileHeight * ACROSS * PIXELS_PER_METRE);
  const canvas = makeCanvas(width, height);
  const roughCanvas = makeCanvas(width, height);
  if (!canvas || !roughCanvas) return null;

  const ctx = canvas.getContext('2d');
  const rough = roughCanvas.getContext('2d');
  if (!ctx || !rough) return null;

  const random = seeded(seed);
  const lip = Math.max(1, Math.round(groutWidth * PIXELS_PER_METRE));

  // Grout first, as a flood; the tiles are laid on top of it and the gaps are
  // simply where they were not laid.
  ctx.fillStyle = grout;
  ctx.fillRect(0, 0, width, height);
  rough.fillStyle = 'rgb(245,245,245)';
  rough.fillRect(0, 0, width, height);

  const base = new THREE.Color(tile);
  const rowHeight = height / ACROSS;
  const tileSpan = width / ACROSS;
  for (let row = 0; row < ACROSS; row++) {
    const shift = row % 2 === 0 ? 0 : tileSpan * offset;
    // One more than fits, so the shifted row still covers the seam it was
    // pushed away from.
    for (let column = -1; column <= ACROSS; column++) {
      const x = column * tileSpan + shift + lip / 2;
      const y = row * rowHeight + lip / 2;
      const w = tileSpan - lip;
      const h = rowHeight - lip;
      const shade = 0.86 + random() * 0.2;
      ctx.fillStyle = `#${base.clone().multiplyScalar(shade).getHexString()}`;
      ctx.fillRect(x, y, w, h);
      // A grubby one now and then.
      if (random() < 0.12) {
        ctx.fillStyle = `rgba(70,60,40,${0.08 + random() * 0.12})`;
        ctx.fillRect(x, y, w, h);
      }
      // The glaze: shiny, but not all equally — some are worn dull.
      const glaze = 0.18 + random() * 0.22 + (random() < 0.1 ? 0.35 : 0);
      const v = Math.round(glaze * 255);
      rough.fillStyle = `rgb(${v},${v},${v})`;
      rough.fillRect(x, y, w, h);
    }
  }

  const finish = (c, srgb) => {
    const texture = new THREE.CanvasTexture(c);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    if (srgb) texture.colorSpace = THREE.SRGBColorSpace;
    // The tiles are small and the wall is long, so this is read at a glancing
    // angle almost all the time — without anisotropy it turns to mush a few
    // metres down the platform.
    texture.anisotropy = 8;
    texture.userData.metres = { x: tileWidth * ACROSS, y: tileHeight * ACROSS };
    return texture;
  };
  const texture = finish(canvas, true);
  texture.roughnessTexture = finish(roughCanvas, false);
  return texture;
}

/**
 * The platform floor (step 98): large stone tiles, each its own shade and
 * speckled, worn smooth along the walking line and dull in the grout, with a
 * roughness map from the same layout. `size` metres a tile; the canvas holds
 * four by four.
 */
export function createFloorTexture({ stone = '#575c68', grout = '#3c4048', size = 0.6, seed = 9 } = {}) {
  const pixels = 256;
  const canvas = makeCanvas(pixels, pixels);
  const roughCanvas = makeCanvas(pixels, pixels);
  if (!canvas || !roughCanvas) return null;
  const ctx = canvas.getContext('2d');
  const rough = roughCanvas.getContext('2d');
  if (!ctx || !rough) return null;
  const random = seeded(seed);
  const across = 4;
  const span = pixels / across;
  ctx.fillStyle = grout;
  ctx.fillRect(0, 0, pixels, pixels);
  rough.fillStyle = 'rgb(250,250,250)';
  rough.fillRect(0, 0, pixels, pixels);
  const base = new THREE.Color(stone);
  for (let row = 0; row < across; row++) {
    for (let column = 0; column < across; column++) {
      const x = column * span + 1.5;
      const y = row * span + 1.5;
      const shade = 0.88 + random() * 0.18;
      ctx.fillStyle = `#${base.clone().multiplyScalar(shade).getHexString()}`;
      ctx.fillRect(x, y, span - 3, span - 3);
      const v = Math.round((0.55 + random() * 0.25) * 255);
      rough.fillStyle = `rgb(${v},${v},${v})`;
      rough.fillRect(x, y, span - 3, span - 3);
    }
  }
  // Terrazzo speckle, and scuffs that are both darker and smoother.
  for (let i = 0; i < 2600; i++) {
    const light = random() < 0.5;
    ctx.fillStyle = light ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)';
    ctx.fillRect(random() * pixels, random() * pixels, 1 + random(), 1 + random());
  }
  for (let i = 0; i < 10; i++) {
    const x = random() * pixels;
    const y = random() * pixels;
    const r = 10 + random() * 30;
    const scuff = ctx.createRadialGradient(x, y, 0, x, y, r);
    scuff.addColorStop(0, 'rgba(10,10,14,0.22)');
    scuff.addColorStop(1, 'rgba(10,10,14,0)');
    ctx.fillStyle = scuff;
    ctx.fillRect(x - r, y - r, r * 2, r * 2);
    const worn = rough.createRadialGradient(x, y, 0, x, y, r);
    worn.addColorStop(0, 'rgba(0,0,0,0.35)');
    worn.addColorStop(1, 'rgba(0,0,0,0)');
    rough.fillStyle = worn;
    rough.fillRect(x - r, y - r, r * 2, r * 2);
  }
  const finish = (c, srgb) => {
    const texture = new THREE.CanvasTexture(c);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    if (srgb) texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 8;
    texture.userData.metres = { x: size * across, y: size * across };
    return texture;
  };
  const texture = finish(canvas, true);
  texture.roughnessTexture = finish(roughCanvas, false);
  return texture;
}

/**
 * Repeats a tile texture across a face of a given size in metres. Pass
 * `texture.roughnessTexture` to get the roughness map repeated the same way.
 */
export function tileFace(texture, width, height) {
  if (!texture) return null;
  const clone = texture.clone();
  clone.needsUpdate = true;
  const metres = texture.userData.metres ?? { x: 1, y: 1 };
  clone.repeat.set(width / metres.x, height / metres.y);
  return clone;
}

function makeCanvas(width, height) {
  if (typeof document === 'undefined' || !document.createElement) return null;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}
